import { resolveDefaultTenantId } from '../config/environment';
/**
 * 🔐 인증 API - 백엔드 /api/system/auth 연동
 * 로그인 → 세션 저장 → API 요청 시 Authorization: Bearer <accessToken|sessionId> 전송
 * 401 응답 시 authFetch.ts 인터셉터가 refresh 후 재시도
 * ✅ UX 안전: fetch 타임아웃 (synaxion-core/04-safety-standards/UX_SAFETY_RULES.md)
 */

import { resolveApiOrigin } from '@shared/api/resolve-api-origin';
import {
  getTenantOAuthCallbackPath,
  TENANT_OAUTH_CENTRAL_CALLBACK_PATH,
  type TenantOAuthCallbackProvider,
  type TenantOAuthMode,
  type TenantOAuthStateIntent,
} from '@shared/auth';
import { buildPlatformUrl, resolveHostContext } from '@shared/tenant-host';
import { resolveTenantRootDomain } from '@shared/tenant-host/defaultRootDomain';
import { API_REQUEST_TIMEOUT_MS } from '../config/api';

const getBaseUrl = () => resolveApiOrigin();

function fetchWithTimeout(url: string, init?: RequestInit): Promise<Response> {
  return fetch(url, {
    ...init,
    signal: init?.signal ?? AbortSignal.timeout(API_REQUEST_TIMEOUT_MS),
  });
}

function unwrapAccessToken(data: Record<string, unknown>): string | undefined {
  if (typeof data.accessToken === 'string') return data.accessToken;
  const nested = data.data;
  if (
    nested &&
    typeof nested === 'object' &&
    typeof (nested as Record<string, unknown>).accessToken === 'string'
  ) {
    return (nested as Record<string, unknown>).accessToken as string;
  }
  return undefined;
}

export interface LoginParams {
  tenantId?: string;
  emailOrUsername: string;
  password: string;
}

export interface LoginResult {
  success: boolean;
  sessionId?: string;
  accessToken?: string;
  refreshToken?: string;
  user?: {
    id: string;
    username: string;
    email: string;
    profile?: unknown;
  };
  error?: string;
}

export interface ValidateSessionResult {
  valid: boolean;
  session?: unknown;
  error?: string;
}

export interface CurrentUserResult {
  success: boolean;
  user?: {
    id: string;
    username: string;
    email: string;
    permissions: string[];
    profile?: unknown;
  };
  error?: string;
}

export interface RefreshSessionResult {
  success: boolean;
  accessToken?: string;
  refreshToken?: string;
  stepUpAt?: string;
  mfaVerifiedAt?: string;
  error?: string;
  code?: string;
}

export interface SsoExchangeParams {
  tenantId?: string;
  handoffToken: string;
  redirectPath?: string;
}

export interface SsoExchangeResult {
  success: boolean;
  sessionId?: string;
  accessToken?: string;
  refreshToken?: string;
  redirectPath?: string;
  error?: string;
  code?: string;
}

export interface TenantAuthPolicyResult {
  success: boolean;
  policy?: {
    allowPlatformSSO: boolean;
    allowLocalPassword: boolean;
    allowedSocialProviders: string[];
    oauthMode?: TenantOAuthMode;
  };
  configuredSocialProviders?: string[];
  error?: string;
}

export interface OAuthHandoffExchangeParams {
  tenantId?: string;
  handoffToken: string;
}

export interface OAuthHandoffExchangeResult {
  success: boolean;
  sessionId?: string;
  accessToken?: string;
  refreshToken?: string;
  returnPath?: string;
  error?: string;
  code?: string;
}

export interface TenantGoogleCentralOAuthResult {
  success: boolean;
  handoffToken?: string;
  returnOrigin?: string;
  returnPath?: string;
  error?: string;
  code?: string;
}

export interface TenantOAuthLoginHintResult {
  success: boolean;
  linkedProviders?: TenantOAuthCallbackProvider[];
  dormantProviders?: TenantOAuthCallbackProvider[];
  error?: string;
}

export interface TenantGoogleOAuthUrlResult {
  ok: boolean;
  authUrl?: string;
  state?: string;
  redirectUri?: string;
  error?: string;
}

export interface TenantGoogleLoginResult {
  success: boolean;
  sessionId?: string;
  accessToken?: string;
  refreshToken?: string;
  user?: LoginResult['user'];
  isNewUser?: boolean;
  error?: string;
  code?: string;
}

/**
 * POST /api/system/auth/login
 */
export async function login(params: LoginParams): Promise<LoginResult> {
  const url = `${getBaseUrl()}/api/system/auth/login`;
  const res = await fetchWithTimeout(url, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({
      tenantId: params.tenantId || resolveDefaultTenantId(),
      emailOrUsername: params.emailOrUsername,
      password: params.password,
    }),
  });
  const data = await res.json();
  if (!res.ok) return { success: false, error: data.error || '로그인 실패' };

  if (data.data && typeof data.data === 'object') {
    const { success: _ignored, ...rest } = data.data as LoginResult;
    return { success: true, ...rest };
  }
  return data;
}

/**
 * POST /api/system/auth/logout
 * sessionId는 jwtUtils.getJWTToken()으로 조회 후 전달하거나, 헤더에 Bearer로 전달
 */
export async function logout(
  sessionId: string,
  tenantId?: string
): Promise<{ success: boolean; message?: string }> {
  const url = `${getBaseUrl()}/api/system/auth/logout`;
  const res = await fetchWithTimeout(url, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      ...(sessionId ? { Authorization: `Bearer ${sessionId}` } : {}),
    },
    body: JSON.stringify({ sessionId, tenantId: tenantId || resolveDefaultTenantId() }),
  });
  return res.json();
}

/**
 * POST /api/system/auth/validate-session
 */
export async function validateSession(
  sessionId: string,
  tenantId?: string
): Promise<ValidateSessionResult> {
  const url = `${getBaseUrl()}/api/system/auth/validate-session`;
  const res = await fetchWithTimeout(url, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      ...(sessionId ? { Authorization: `Bearer ${sessionId}` } : {}),
    },
    body: JSON.stringify({ sessionId, tenantId: tenantId || resolveDefaultTenantId() }),
  });
  return res.json();
}

/**
 * GET /api/system/auth/current-user
 */
export async function getCurrentUser(
  sessionId: string,
  tenantId?: string
): Promise<CurrentUserResult> {
  const url = `${getBaseUrl()}/api/system/auth/current-user?tenantId=${encodeURIComponent(tenantId || resolveDefaultTenantId())}`;
  const res = await fetchWithTimeout(url, {
    headers: sessionId ? { Authorization: `Bearer ${sessionId}` } : {},
  });
  return res.json();
}

/**
 * POST /api/system/auth/refresh
 * sessionId로 accessToken 갱신 (401 인터셉터에서 호출)
 */
export async function refreshSession(
  sessionId: string,
  tenantId?: string
): Promise<RefreshSessionResult> {
  const url = `${getBaseUrl()}/api/system/auth/refresh`;
  const res = await fetchWithTimeout(url, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({
      sessionId,
      tenantId: tenantId || resolveDefaultTenantId(),
    }),
  });
  const data = (await res.json()) as Record<string, unknown>;
  if (!res.ok) {
    return {
      success: false,
      error: typeof data.error === 'string' ? data.error : '세션 갱신 실패',
    };
  }

  const accessToken = unwrapAccessToken(data);
  if (!accessToken) {
    return { success: false, error: '갱신된 accessToken이 없습니다' };
  }

  return { success: true, accessToken };
}

export interface StepUpSessionParams {
  tenantId?: string;
  password?: string;
  otpCode?: string;
}

export async function stepUpSession(params: StepUpSessionParams): Promise<RefreshSessionResult> {
  const url = `${getBaseUrl()}/api/auth/step-up`;
  const token = localStorage.getItem('inflomatrix_jwt_token');
  const sessionId = localStorage.getItem('inflomatrix_session_id');
  const bearer = token || sessionId || '';
  const res = await fetchWithTimeout(url, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      ...(bearer ? { Authorization: `Bearer ${bearer}` } : {}),
    },
    body: JSON.stringify({
      tenantId: params.tenantId || resolveDefaultTenantId(),
      ...(params.password?.trim() ? { password: params.password.trim() } : {}),
      ...(params.otpCode?.trim() ? { otpCode: params.otpCode.trim() } : {}),
    }),
  });

  const data = (await res.json()) as Record<string, unknown>;
  if (!res.ok) {
    return {
      success: false,
      error: typeof data.error === 'string' ? data.error : '추가 인증에 실패했습니다',
      ...(typeof data.code === 'string' ? { code: data.code } : {}),
    };
  }

  return {
    success: true,
    ...(typeof data.accessToken === 'string' ? { accessToken: data.accessToken } : {}),
    ...(typeof data.refreshToken === 'string' ? { refreshToken: data.refreshToken } : {}),
    ...(typeof data.stepUpAt === 'string' ? { stepUpAt: data.stepUpAt } : {}),
    ...(typeof data.mfaVerifiedAt === 'string' ? { mfaVerifiedAt: data.mfaVerifiedAt } : {}),
  };
}

/**
 * POST /api/system/auth/sso-exchange — Platform handoff → tenant session
 */
export async function exchangeSsoHandoff(params: SsoExchangeParams): Promise<SsoExchangeResult> {
  const url = `${getBaseUrl()}/api/system/auth/sso-exchange`;
  const res = await fetchWithTimeout(url, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({
      tenantId: params.tenantId || resolveDefaultTenantId(),
      handoffToken: params.handoffToken,
      ...(params.redirectPath ? { redirectPath: params.redirectPath } : {}),
    }),
  });
  const data = (await res.json()) as SsoExchangeResult & { error?: string; code?: string };
  if (!res.ok) {
    return {
      success: false,
      error: data.error ?? 'SSO 교환 실패',
      ...(data.code ? { code: data.code } : {}),
    };
  }
  return data;
}

/**
 * GET /api/system/auth/policy — tenant login auth policy
 */
export async function fetchTenantAuthPolicy(tenantId?: string): Promise<TenantAuthPolicyResult> {
  const tid = tenantId || resolveDefaultTenantId();
  const url = `${getBaseUrl()}/api/system/auth/policy?tenantId=${encodeURIComponent(tid)}`;
  try {
    const res = await fetchWithTimeout(url);
    const data = (await res.json()) as TenantAuthPolicyResult;
    if (!res.ok) {
      return { success: false, error: data.error ?? '인증 정책 조회 실패' };
    }
    return data;
  } catch {
    return { success: false, error: '인증 정책을 불러오지 못했습니다.' };
  }
}

/**
 * GET /api/system/auth/oauth/login-hint — dormant oauth_accounts probe
 */
export async function fetchTenantOAuthLoginHint(params: {
  tenantId?: string;
  email: string;
}): Promise<TenantOAuthLoginHintResult> {
  const tid = params.tenantId || resolveDefaultTenantId();
  const email = params.email.trim();
  if (!email.includes('@')) {
    return { success: true, linkedProviders: [], dormantProviders: [] };
  }
  const url = `${getBaseUrl()}/api/system/auth/oauth/login-hint?tenantId=${encodeURIComponent(tid)}&email=${encodeURIComponent(email)}`;
  try {
    const res = await fetchWithTimeout(url);
    const body = (await res.json()) as {
      success?: boolean;
      data?: {
        linkedProviders?: TenantOAuthCallbackProvider[];
        dormantProviders?: TenantOAuthCallbackProvider[];
      };
      error?: string;
    };
    if (!res.ok || body.success === false) {
      return { success: false, error: body.error ?? 'OAuth 로그인 힌트 조회 실패' };
    }
    return {
      success: true,
      linkedProviders: body.data?.linkedProviders ?? [],
      dormantProviders: body.data?.dormantProviders ?? [],
    };
  } catch {
    return { success: false, error: 'OAuth 로그인 힌트를 불러오지 못했습니다.' };
  }
}

function resolveBrowserPlatformOrigin(): string {
  if (typeof window === 'undefined') {
    return '';
  }

  const rootDomain = resolveTenantRootDomain(import.meta.env.VITE_TENANT_ROOT_DOMAIN);
  const hostContext = resolveHostContext({
    hostname: window.location.hostname,
    rootDomain: import.meta.env.VITE_TENANT_ROOT_DOMAIN,
  });

  if (hostContext.kind === 'platform') {
    return window.location.origin;
  }

  if (window.location.hostname.endsWith('.localhost')) {
    const port = window.location.port ? `:${window.location.port}` : '';
    return `${window.location.protocol}//localhost${port}`;
  }

  const scheme = window.location.protocol.replace(':', '') as 'http' | 'https';
  return buildPlatformUrl(rootDomain, '', scheme).replace(/\/$/, '');
}

export function getTenantGoogleOAuthCentralRedirectUri(): string {
  return getTenantOAuthRedirectUri('google', 'platform_managed');
}

export function getTenantGoogleOAuthRedirectUri(
  oauthMode: TenantOAuthMode = 'platform_managed'
): string {
  return getTenantOAuthRedirectUri('google', oauthMode);
}

export function getTenantOAuthRedirectUri(
  provider: 'google' | 'apple' | 'kakao',
  oauthMode: TenantOAuthMode = 'platform_managed'
): string {
  if (oauthMode === 'platform_managed') {
    const platformOrigin = resolveBrowserPlatformOrigin();
    if (platformOrigin) {
      return `${platformOrigin}${TENANT_OAUTH_CENTRAL_CALLBACK_PATH}/${provider}`;
    }
  }

  const origin = typeof window !== 'undefined' ? window.location.origin : '';
  return `${origin}${getTenantOAuthCallbackPath(provider)}`;
}

function resolveOAuthReturnOrigin(): string {
  if (typeof window !== 'undefined') {
    return window.location.origin;
  }
  return '';
}

function resolveDefaultOAuthReturnPath(): string {
  if (typeof window === 'undefined') {
    return '/workspace';
  }
  return window.location.pathname.startsWith('/login') ? '/workspace' : window.location.pathname;
}

/**
 * GET /api/system/auth/oauth/google/url
 */
export async function getTenantGoogleOAuthUrl(params: {
  tenantId?: string;
  inviteToken?: string;
  email?: string;
  oauthMode?: TenantOAuthMode;
  returnPath?: string;
  intent?: TenantOAuthStateIntent;
}): Promise<TenantGoogleOAuthUrlResult> {
  const tid = params.tenantId || resolveDefaultTenantId();
  const oauthMode = params.oauthMode ?? 'platform_managed';
  const search = new URLSearchParams({
    tenantId: tid,
    redirectUri: getTenantOAuthRedirectUri('google', oauthMode),
  });
  if (oauthMode === 'platform_managed') {
    search.set('returnOrigin', resolveOAuthReturnOrigin());
    search.set('returnPath', params.returnPath?.trim() || resolveDefaultOAuthReturnPath());
    search.set('intent', params.intent ?? 'login');
  }
  if (params.inviteToken?.trim()) {
    search.set('inviteToken', params.inviteToken.trim());
  }
  if (params.email?.trim()) {
    search.set('email', params.email.trim());
  }
  const url = `${getBaseUrl()}/api/system/auth/oauth/google/url?${search.toString()}`;
  try {
    const res = await fetchWithTimeout(url);
    const body = (await res.json()) as {
      success?: boolean;
      data?: { authUrl: string; state: string; redirectUri: string };
      error?: string;
    };
    if (res.ok && body.data?.authUrl) {
      return { ok: true, ...body.data };
    }
    return { ok: false, error: body.error ?? 'Google OAuth URL을 가져오지 못했습니다.' };
  } catch {
    return { ok: false, error: 'API에 연결할 수 없습니다.' };
  }
}

/**
 * POST /api/system/auth/oauth-handoff — central callback handoff → tenant session
 */
export async function exchangeOAuthHandoff(
  params: OAuthHandoffExchangeParams
): Promise<OAuthHandoffExchangeResult> {
  const url = `${getBaseUrl()}/api/system/auth/oauth-handoff`;
  const res = await fetchWithTimeout(url, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({
      tenantId: params.tenantId || resolveDefaultTenantId(),
      handoffToken: params.handoffToken,
    }),
  });
  const data = (await res.json()) as OAuthHandoffExchangeResult & { error?: string; code?: string };
  if (!res.ok) {
    return {
      success: false,
      error: data.error ?? 'OAuth handoff 교환 실패',
      ...(data.code ? { code: data.code } : {}),
    };
  }
  return data;
}

/**
 * POST /api/system/auth/oauth/google — platform central callback (handoff mint)
 */
export async function completeTenantGoogleOAuthCentralCallback(input: {
  code: string;
  state: string;
  redirectUri: string;
  tenantId?: string;
  inviteToken?: string;
}): Promise<TenantGoogleCentralOAuthResult> {
  const url = `${getBaseUrl()}/api/system/auth/oauth/google`;
  const res = await fetchWithTimeout(url, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({
      ...(input.tenantId ? { tenantId: input.tenantId } : {}),
      code: input.code,
      redirectUri: input.redirectUri,
      state: input.state,
      ...(input.inviteToken ? { inviteToken: input.inviteToken } : {}),
    }),
  });
  const data = (await res.json()) as TenantGoogleCentralOAuthResult & {
    data?: TenantGoogleCentralOAuthResult;
    error?: string;
    code?: string;
  };
  if (!res.ok) {
    return {
      success: false,
      error: data.error ?? 'Google 로그인 실패',
      ...(data.code ? { code: data.code } : {}),
    };
  }

  const payload = data.data ?? data;
  if (!payload.handoffToken || !payload.returnOrigin) {
    return {
      success: false,
      error: 'OAuth handoff 응답이 올바르지 않습니다.',
      code: 'INVALID_HANDOFF_RESPONSE',
    };
  }

  return {
    success: true,
    handoffToken: payload.handoffToken,
    returnOrigin: payload.returnOrigin,
    ...(payload.returnPath !== undefined ? { returnPath: payload.returnPath } : {}),
  };
}

/**
 * POST /api/system/auth/oauth/google — tenant-owned origin callback (direct session)
 */
export async function loginWithGoogleOAuth(input: {
  tenantId?: string;
  code: string;
  redirectUri: string;
  state: string;
  inviteToken?: string;
}): Promise<TenantGoogleLoginResult> {
  const url = `${getBaseUrl()}/api/system/auth/oauth/google`;
  const res = await fetchWithTimeout(url, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({
      tenantId: input.tenantId || resolveDefaultTenantId(),
      code: input.code,
      redirectUri: input.redirectUri,
      state: input.state,
      ...(input.inviteToken ? { inviteToken: input.inviteToken } : {}),
    }),
  });
  const data = (await res.json()) as TenantGoogleLoginResult & { error?: string; code?: string };
  if (!res.ok) {
    return {
      success: false,
      error: data.error ?? 'Google 로그인 실패',
      ...(data.code ? { code: data.code } : {}),
    };
  }
  return data;
}

type TenantOAuthUrlParams = {
  tenantId?: string;
  inviteToken?: string;
  oauthMode?: TenantOAuthMode;
  returnPath?: string;
  intent?: TenantOAuthStateIntent;
};

async function fetchTenantOAuthUrl(
  provider: 'apple' | 'kakao',
  params: TenantOAuthUrlParams
): Promise<TenantGoogleOAuthUrlResult> {
  const tid = params.tenantId || resolveDefaultTenantId();
  const oauthMode = params.oauthMode ?? 'platform_managed';
  const search = new URLSearchParams({
    tenantId: tid,
    redirectUri: getTenantOAuthRedirectUri(provider, oauthMode),
  });
  if (oauthMode === 'platform_managed') {
    search.set('returnOrigin', resolveOAuthReturnOrigin());
    search.set('returnPath', params.returnPath?.trim() || resolveDefaultOAuthReturnPath());
    search.set('intent', params.intent ?? 'login');
  }
  if (params.inviteToken?.trim()) {
    search.set('inviteToken', params.inviteToken.trim());
  }
  const url = `${getBaseUrl()}/api/system/auth/oauth/${provider}/url?${search.toString()}`;
  try {
    const res = await fetchWithTimeout(url);
    const body = (await res.json()) as {
      success?: boolean;
      data?: { authUrl: string; state: string; redirectUri: string };
      error?: string;
    };
    if (res.ok && body.data?.authUrl) {
      return { ok: true, ...body.data };
    }
    return { ok: false, error: body.error ?? `${provider} OAuth URL을 가져오지 못했습니다.` };
  } catch {
    return { ok: false, error: 'API에 연결할 수 없습니다.' };
  }
}

/** GET /api/system/auth/oauth/apple/url — Phase 5 scaffold */
export async function getTenantAppleOAuthUrl(
  params: TenantOAuthUrlParams
): Promise<TenantGoogleOAuthUrlResult> {
  return fetchTenantOAuthUrl('apple', params);
}

/** GET /api/system/auth/oauth/kakao/url — Phase 5 scaffold */
export async function getTenantKakaoOAuthUrl(
  params: TenantOAuthUrlParams
): Promise<TenantGoogleOAuthUrlResult> {
  return fetchTenantOAuthUrl('kakao', params);
}

type TenantOAuthLoginParams = {
  tenantId?: string;
  code: string;
  redirectUri: string;
  state: string;
  inviteToken?: string;
};

async function postTenantOAuthLogin(
  provider: 'apple' | 'kakao',
  input: TenantOAuthLoginParams
): Promise<TenantGoogleLoginResult> {
  const url = `${getBaseUrl()}/api/system/auth/oauth/${provider}`;
  const res = await fetchWithTimeout(url, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({
      tenantId: input.tenantId || resolveDefaultTenantId(),
      code: input.code,
      redirectUri: input.redirectUri,
      state: input.state,
      ...(input.inviteToken ? { inviteToken: input.inviteToken } : {}),
    }),
  });
  const data = (await res.json()) as TenantGoogleLoginResult & { error?: string; code?: string };
  if (!res.ok) {
    return {
      success: false,
      error: data.error ?? `${provider} 로그인 실패`,
      ...(data.code ? { code: data.code } : {}),
    };
  }
  return data;
}

/** POST /api/system/auth/oauth/apple — Phase 5 scaffold */
export async function loginWithAppleOAuth(
  input: TenantOAuthLoginParams
): Promise<TenantGoogleLoginResult> {
  return postTenantOAuthLogin('apple', input);
}

/** POST /api/system/auth/oauth/kakao — Phase 5 scaffold */
export async function loginWithKakaoOAuth(
  input: TenantOAuthLoginParams
): Promise<TenantGoogleLoginResult> {
  return postTenantOAuthLogin('kakao', input);
}

export async function setPasswordFromInvite(input: {
  token: string;
  password: string;
}): Promise<{ success: boolean; error?: string; code?: string }> {
  const res = await fetchWithTimeout(`${getBaseUrl()}/api/system/auth/set-password-from-invite`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(input),
  });
  const data = (await res.json()) as { success?: boolean; error?: string; code?: string };
  if (!res.ok) {
    return {
      success: false,
      error: data.error ?? '비밀번호 설정에 실패했습니다',
      ...(data.code ? { code: data.code } : {}),
    };
  }
  return { success: true };
}
